/**
 * 🔒 How a rendered sheet is read on screen (decision 0611, amending 0563 and
 * 0577): the pane is a frame of its own size that scrolls inside itself, a sheet
 * opens with the whole of its first page visible, and a zoom step scales the
 * paper inside the frame rather than the frame.
 *
 * 🔨 The arithmetic lives here, once, because two panes read sheets — the
 * viewer every document's PDF opens in and the print template editor's preview —
 * and a second copy of it is a second answer to *what is fit page*.
 *
 * 💰 The viewer used to point a frame at the blob and let the browser's own
 * plug-in decide the scale, so a page break moved the viewing window and no
 * control could put it back. The product owner: *"the scaling for the PDF view
 * needs to be fit page default then one can zoom in; the current page breaks and
 * it changes the viewing window."*
 */
import { POINTS_PER_INCH } from './pagination';

/**
 * 🔒 A CSS inch, which is what a canvas sized in a page's inches is measured in.
 * A browser defines 1in as 96px whatever the screen's own density, so a sheet
 * drawn at scale 1 is a sheet at its paper size as the browser reckons it.
 */
export const CSS_PIXELS_PER_INCH = 96;

/**
 * 🔒 The zoom steps a sheet is read at: six quarters from half size to double,
 * the set decision 0577 fixed for the template preview and now the viewer's too.
 * 🔨 100 is the fit the reader chose — the whole page, or the page's width — so
 * the reset is one number and never a second measurement that can disagree.
 */
export const SHEET_ZOOM_STEPS = [50, 75, 100, 125, 150, 175, 200] as const;
export const SHEET_ZOOM_FIT = 100;

export type SheetZoom = (typeof SHEET_ZOOM_STEPS)[number];

/** Which measurement 100 means: the whole page inside the pane, or the page's width across it. */
export type SheetFit = 'page' | 'width';

/** One page's paper size, in inches. */
export interface SheetPageInches {
  widthInches: number;
  heightInches: number;
}

/** The pane the sheet is read in, in CSS pixels; a box nothing has laid out yet measures zero. */
export interface SheetPaneBox {
  widthPx: number;
  heightPx: number;
}

/** A length in PDF points as inches: a point is a seventy-second of an inch, which is what a page's own box is written in. */
export function sheetInchesOfPoints(points: number): number {
  return points / POINTS_PER_INCH;
}

/**
 * 🔒 The scale the whole page is visible at inside the pane: the smaller of the
 * two fits, so neither edge of the paper is outside the frame. Fit width takes
 * the width alone and lets the page run past the foot of the pane, which the
 * pane then scrolls.
 *
 * 🔨 A pane nothing has measured yet — a box of zero, which is every pane before
 * its first layout and every pane under a test runner that lays nothing out —
 * reads as the sheet's natural size rather than as a scale of zero or infinity.
 * A sheet drawn at nothing is a blank region, and a blank region names no reason.
 */
export function fitScale(page: SheetPageInches, pane: SheetPaneBox, fit: SheetFit): number {
  const width = page.widthInches * CSS_PIXELS_PER_INCH;
  const height = page.heightInches * CSS_PIXELS_PER_INCH;
  if (!(width > 0) || !(height > 0)) return 1;
  const across = pane.widthPx > 0 ? pane.widthPx / width : null;
  const down = pane.heightPx > 0 ? pane.heightPx / height : null;
  if (fit === 'width') return across ?? 1;
  if (across === null || down === null) return across ?? down ?? 1;
  return Math.min(across, down);
}

/**
 * The scale one page draws at: the reader's fit, times the step they zoomed to.
 * 🔨 The pane's own box is not in the answer, which is the point — the frame
 * keeps its size at every step and the paper inside it grows.
 */
export function sheetScaleAt(page: SheetPageInches, pane: SheetPaneBox, fit: SheetFit, zoom: number): number {
  return fitScale(page, pane, fit) * (zoom / 100);
}

/** One page's drawn size in CSS pixels, at a scale. */
export function sheetPagePixels(page: SheetPageInches, scale: number): { widthPx: number; heightPx: number } {
  return {
    widthPx: Math.max(1, Math.round(page.widthInches * CSS_PIXELS_PER_INCH * scale)),
    heightPx: Math.max(1, Math.round(page.heightInches * CSS_PIXELS_PER_INCH * scale)),
  };
}

/**
 * The step in or out from here, or null at the end of the scale. 🔨 A step off
 * the end is null rather than the end again, so a control that cannot act is
 * dead and says why (decision 0559) instead of silently doing nothing.
 */
export function nextSheetZoom(zoom: number, by: 1 | -1): SheetZoom | null {
  const at = SHEET_ZOOM_STEPS.indexOf(zoom as SheetZoom);
  const from = at < 0 ? SHEET_ZOOM_STEPS.indexOf(SHEET_ZOOM_FIT) : at;
  return SHEET_ZOOM_STEPS[from + by] ?? null;
}
